import * as  React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as configActions from '../../../../../redux/config/actions.config';
import * as _ from 'lodash';
import * as projectActions from '../../../../../redux/projects/actions.projects';
import asyncDispatch from '../../../../../services/mini/asyncDispatch';
import ToasterService from '../../../../../services/ToasterService';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import TextFieldWrapper from '../../../../components/TextFieldWrapper/TextFieldWrapper';
import { Loader, LoaderSize, LoaderTheme } from '../../../../components/Loader/Loader';
import TextFieldWithJsonWrapper from '../../../../components/TextFieldWithJsonWrapper/TextFieldWithJsonWrapper';
import { ISaveWakeWordParam } from '../../../../../redux/projects/saga.projects';
import * as moment from 'moment';
import formats from '../../../../../const/formats';
import { formatJsonForWakeWordModel } from '../../../../../services/mini/formatJsonForWakeWordModel';
import { cleanupWakeWordOptions } from '../../../../../services/mini/cleanupWakeWordOptions';
import { IAcusticModel } from '../../../../../models/IAcusticModel';
import { IWakeWord } from '../../../../../models/IWakeWord';
import nextEntityNameGenerator from '../../../../../services/mini/nextEntityNameGenerator';

type WakeWordFormProps = WakeWordFormReduxProps & {
    projectId: string
    wakeWords: IWakeWord[]
    wakeWord: IWakeWord
    updateMode: boolean
    onCancel: () => void;
    onAfterSave: (wakeWord: IWakeWord) => Promise<void>;
}

function WakeWordForm({ projectId, acousticModels, wakeWords, wakeWord, updateMode, defaultWakeWordOptions, getWakeWordAcusticModelsConfig, saveWakeWord, onAfterSave, onCancel }: WakeWordFormProps) {
    const [saving, setSaving] = React.useState(false);
    const [wakeWordNameValid, setWakeWordNameValid] = React.useState(true);

    const [wakeWordName, setWakeWordName] = React.useState(
        updateMode ?
            wakeWord.name :
            (wakeWords?.length === 1 ? 'My custom Wake word 1' : nextEntityNameGenerator(wakeWords[wakeWords.length - 1].name)));

    const [wakeWordNameFieldError, setModelNameFieldError] = React.useState(null);
    const [wakeWordNameFieldInfo, setModelNameFieldInfo] = React.useState(null);
    const [acoustic, setAcoustic] = React.useState(null);

    let wakeWordOptions = null;

    if (wakeWord) {
        wakeWordOptions = cleanupWakeWordOptions(JSON.parse(wakeWord.options));
    }
    const [modelJson, setModelJson] = React.useState(!_.isEmpty(wakeWordOptions) ?
        formatJsonForWakeWordModel(JSON.stringify(wakeWordOptions)) :
        formatJsonForWakeWordModel(defaultWakeWordOptions));
    const [modelJsonValid, setModelJsonValid] = React.useState(true);

    React.useEffect(() => {
        getWakeWordAcusticModelsConfig().then((accModels: IAcusticModel[]) => {
            if (acoustic === null && accModels.length > 0) {
                if (wakeWord) {
                    const wakeWordAcoustic = (JSON.parse(wakeWord.options) as IAcusticModel).acoustic;
                    if (wakeWordAcoustic) {
                        setAcoustic(wakeWordAcoustic);
                    } else {
                        setAcoustic(accModels[0].acoustic);
                    }
                } else {
                    setAcoustic(accModels[0].acoustic);
                }
            }
        });
    }, []);

    const onChangeCb = React.useCallback((val) => {
        setModelJson(val);
    }, []);

    const onValidateCb = React.useCallback((isValid) => {
        setModelJsonValid(isValid);
    }, []);


    const onWakeWordNameValidateCb = React.useCallback((e) => {
        let nameFieldError = null;
        let nameFieldInfo = null;

        const wakeWordsForNameCheck = updateMode ? wakeWords.map(w => w.id !== wakeWord.id) : wakeWords;

        if (wakeWordName.trim().length === 0) {
            nameFieldError = 'Wake word name cannot be empty';
        }

        if (_.find(wakeWordsForNameCheck, { name: wakeWordName })) {
            nameFieldError = 'Wake word with such name already exists';
        }

        if (wakeWordName.length > 50) {
            nameFieldInfo = '`Max wake word name length: 50 characters`';
        }
        if (nameFieldError || nameFieldInfo) {
            setWakeWordNameValid(false);
        } else {
            setWakeWordNameValid(true);
        }
        setModelNameFieldError(nameFieldError);
        setModelNameFieldInfo(nameFieldInfo);


    }, [updateMode, wakeWord.id, wakeWordName, wakeWords]);

    const onWakeWordNameChangeCb = React.useCallback((e) => {
        setWakeWordName(e.target.value);
    }, []);

    const saveWakeWordCb = React.useCallback(() => {
        const acousticModel = _.find(acousticModels, { acoustic }) as IAcusticModel;

        if (wakeWordName && acousticModel) {
            setSaving(true);

            saveWakeWord({
                projectId,
                wakeWordName,
                wakeWordId: updateMode ? wakeWord.id : null,
                options: {
                    acoustic: acousticModel.acoustic,
                    ...(JSON.parse(modelJson))
                },
                acousticName: acousticModel.name
            }).then((wakeWord: IWakeWord) => {
                return onAfterSave(wakeWord).then(() => {
                    setSaving(false);
                    ToasterService.showInfo({ title: 'Wake word model was saved and selected.' });
                });
            }).catch(() => {
                setSaving(false);
            });
        }
    }, [acoustic, acousticModels, modelJson, onAfterSave, projectId, saveWakeWord, updateMode, wakeWord.id, wakeWordName]);

    return <div className="wakeword-form">
        <div className="wakeword-form__title">{`${!updateMode ? 'Create new' : 'Edit'} custom wake word`}</div>
        <div className="wakeword-form__body">
            {acoustic === null ?
                <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} /> :
                <React.Fragment>
                    <div className={saving ? 'disabled' : ''}>
                        <div>
                            <TextFieldWrapper
                                placeholder="Wake word name"
                                label="Wake word name"
                                name="wakeWordName"
                                id="nawakeWordNameme"
                                required={true}
                                autoFocus={true}
                                value={wakeWordName}
                                error={wakeWordNameFieldError}
                                info={wakeWordNameFieldInfo}
                                onChange={onWakeWordNameChangeCb}
                                onBlur={onWakeWordNameValidateCb}
                                onLengthValidate={onWakeWordNameValidateCb}
                            />
                        </div>
                        <br />
                        <div>
                            <label className="wakeword-form__label">Acoustic model:</label>
                            <ComboBox
                                values={acousticModels}
                                valueKey="acoustic"
                                valueName="name"
                                selectedValueId={acoustic}
                                renderItem={(acusticModelItem: IAcusticModel) => {
                                    const modelDateTime = acusticModelItem.date ? `${moment(acusticModelItem.date).format(formats.MM_DD_YY_hh_mm_a)} - ` : '';
                                    return <div style={{ paddingLeft: '12px' }}>
                                        {modelDateTime}{acusticModelItem.name}
                                    </div>;
                                }}
                                onValueSelect={(acusticModelItem) => {
                                    setAcoustic(acusticModelItem.acoustic);
                                }}
                            />
                        </div>
                        <br />
                        <div>
                            <label className="wakeword-form__label">Wake word model:</label>
                            <TextFieldWithJsonWrapper
                                className="wakeword-form__editor"
                                value={modelJson}
                                noAutoFocus={true}
                                placeholder="Enter auth data (JSON object or string)"
                                onChange={onChangeCb}
                                onValidate={onValidateCb} />
                        </div>
                    </div>
                    <div className="wakeword-form__btns">
                        <div className={`action-btn wakeword-form__create-btn ${(!modelJsonValid || !wakeWordNameValid) && 'disabled'} ${saving && 'btn-loader'}`}
                            onClick={saveWakeWordCb}>
                            {`${!updateMode ? 'Create' : 'Update'} wake word`}
                        </div>
                        <div className={`action-btn cancl-action light-blue-with-border wakeword-form__cancel-btn ${saving && 'disabled'}`}
                            onClick={onCancel}>
                            Cancel
                        </div>
                    </div>
                </React.Fragment>}
        </div>
    </div>;
}


function mapStateToProps({ config, projectsInfo }) {
    return {
        acousticModels: config.acousticModels,
        defaultWakeWordOptions: projectsInfo.currentProject.defaultWakeWordOptions,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        saveWakeWord: (param: ISaveWakeWordParam) => asyncDispatch(projectActions.saveWakeWord, dispatch, param),
        getWakeWordAcusticModelsConfig: () => asyncDispatch(configActions.getWakeWordAcusticModelsConfig, dispatch),
    };
}


const connector = connect(mapStateToProps, mapDispatchToProps);

type WakeWordFormReduxProps = ConnectedProps<typeof connector>;

export default connector(WakeWordForm);